import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { DTypeStatus } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { debounceTime, firstValueFrom, Observable, of, switchMap } from 'rxjs';

import { MessagesService } from '../messages.service';
import { Message } from '../messages.types';
import { DtypeComponentListComponent } from '../../../../core/dtype/dtype-component-list/dtype-component-list.component';
import { CompanyUser } from '../../../../core/user/companyUser.model';
import { CompanyService } from '../../../../core/company/company.service';
import { DTypeService } from '../../../../core/dtype/dtype.service';
import { DialogsService } from '../../../../dialogs/dialogs.service';
import { FuseConfirmationService } from '../../../../../@fuse/services/confirmation/confirmation.service';
import { UserService } from '../../../../core/user/user.service';
import { ServicesService } from '../../../../modules/admin/services/services.service';
import { TasksService } from '../../../../modules/admin/tasks/tasks.service';
import { SendMessageBody } from '../../../../modules/admin/tasks/tasks.types';
import { LocaleString } from '../../../../core/translate/localeString.model';

@Component({
    selector: 'create-message',
    templateUrl: './create-message.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateMessageComponent implements OnInit {

    @ViewChild('componentList') componentList: DtypeComponentListComponent;
    @Input() message: Message;
    @Output() readonly closePanel: EventEmitter<void> = new EventEmitter<void>();

    dTypeSendForm: DTypeForm;

    users: Observable<CompanyUser[]>;

    invalidUser: boolean = false;

    loading = true;

    serviceId: string;
    taskId: string;
    messageFromTask: boolean;

    constructor(
        private companyService: CompanyService,
        private dTypeService: DTypeService,
        private dialogsService: DialogsService,
        private _fuseConfirmationService: FuseConfirmationService,
        private messagesService: MessagesService,
        private userService: UserService,
        private translocoService: TranslocoService,
        private servicesService: ServicesService,
        private taskService: TasksService
    ) { }

    async ngOnInit() {
        this.dTypeSendForm = await this.dTypeService.getDtypeFormByUri({
            uri: this.messagesService.sendNotificationUri,
            status: DTypeStatus.LIVE,
            bindableOptions: true
        }, this.message, { readonly: false, isUpdate: false });

        this.addNoDTypeControls(this.dTypeSendForm);

        this.users = this.getControl('to').valueChanges.pipe(
            debounceTime(1000),
            switchMap((value) => {
                if (typeof value === 'string' && value.length >= 3) {
                    return this.userService.searchCompanyUser(value, this.companyService.missionCompanyId);
                }
                return of([]);
            })
        );
        this.loading = false;

        this.getControl('to').valueChanges.subscribe((value) => {
            if(typeof value == typeof 'asd') {
                this.getControl('to').setErrors({'incorrect': true});
                this.invalidUser = true;
            } else {
                this.invalidUser = false;
            }
        });

        this.messageFromTask = await firstValueFrom(this.messagesService.messagesFromTask$);
        if (this.messageFromTask) {
            this.serviceId = (await firstValueFrom(this.servicesService.service$)).serviceId;
            this.taskId = (await firstValueFrom(this.taskService.task$)).task.taskId;
        }

    }

    addNoDTypeControls(form: DTypeForm) {
        form.control.addControl('to', new FormControl(this.message.to, Validators.required));
        form.control.addControl('priority', new FormControl(this.message.priority ?? 'high', Validators.required));
        form.control.addControl('messageType', new FormControl('push_notification'));
    }

    displayFn(user: CompanyUser) {
        return user && user.displayName ? user.displayName : '';
    }

    getControl(name: string) {
        return this.dTypeSendForm.control.get(name);
    }

    sendPush() {
        if (this.validate()) {
            const dialogRef = this._fuseConfirmationService.open({
                title: this.translocoService.translate('warning'),
                message: this.translocoService.translate('sendPushConfirm'),
                actions: {
                    confirm: {
                        label: this.translocoService.translate('confirm')
                    },
                    cancel: {
                        label: this.translocoService.translate('cancel')
                    }
                }
            });
            dialogRef.afterClosed().subscribe(async (result) => {
                if (result === 'confirmed') {
                    const message = this.dTypeSendForm.control.getRawValue();
                    if (this.messageFromTask) {
                        const body: SendMessageBody = {
                            taskId: this.taskId,
                            serviceId: this.serviceId,
                            notification: message
                        };
                        await firstValueFrom(this.taskService.sendMessage(body));
                        this.closePanel.emit();
                    } else if (typeof message.to === 'string') {
                        await firstValueFrom(this.messagesService.create({ ...message }));
                    } else {
                        await firstValueFrom(this.messagesService.create({ ...message, to: message.to.userId }));
                    }
                    this.messagesService.goToListView();
                }
            });
        } else {
            this.dialogsService.openErrorDialog({
                message: this.translocoService.translate('errorsInForm')
            });
        }
    }

    private validate() {
        this.dTypeSendForm.control.markAllAsTouched();
        return this.dTypeSendForm.control.valid && !this.localeIsEmpty(this.dTypeSendForm.control.get('title').value)
            && !this.localeIsEmpty(this.dTypeSendForm.control.get('message').value);
    }

    private localeIsEmpty(locale: LocaleString) {
        for (const key of Object.keys(locale)) {
            if (locale[key]) {
                return false;
            }
        }
        return true;
    }

}
